import React, { useState, useEffect } from 'react';
import { getDailyStats } from '../../api/campaign';

const ScratchCardStats = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [filter, setFilter] = useState('all');

  // Fetch data from API
  const fetchData = async (date) => {
    setLoading(true);
    try {
      setData(await getDailyStats(date));
    } catch (error) {
      console.error('Error fetching data:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(selectedDate);
  }, [selectedDate]);

  // Get row color based on win_count
  const getRowColor = (winCount) => {
    if (winCount >= 10) return 'bg-red-50 hover:bg-red-100';
    if (winCount >= 8) return 'bg-orange-50 hover:bg-orange-100';
    if (winCount >= 6) return 'bg-yellow-50 hover:bg-yellow-100';
    if (winCount >= 4) return 'bg-blue-50 hover:bg-blue-100';
    return 'bg-gray-50 hover:bg-gray-100';
  };

  // Get badge color
  const getBadgeColor = (winCount) => {
    if (winCount >= 10) return 'bg-red-500 text-white';
    if (winCount >= 8) return 'bg-orange-500 text-white';
    if (winCount >= 6) return 'bg-yellow-500 text-white';
    if (winCount >= 4) return 'bg-blue-500 text-white';
    return 'bg-gray-400 text-white';
  };

  // Filter data with ranges
  const filteredData = data.filter(item => {
    if (filter === 'all') return true;
    if (filter === 'gte10') return item.win_count >= 10; // >= 10
    if (filter === 'r8to9') return item.win_count >= 8 && item.win_count < 10; // 8-9
    if (filter === 'r6to7') return item.win_count >= 6 && item.win_count < 8; // 6-7
    if (filter === 'r4to5') return item.win_count >= 4 && item.win_count < 6; // 4-5
    if (filter === 'lt4') return item.win_count < 4; // < 4
    return true;
  });

  const filterButtons = [
    { id: 'all', label: 'All Shops', color: 'bg-gray-600 hover:bg-gray-700' },
    { id: 'gte10', label: '≥ 10 Wins', color: 'bg-red-600 hover:bg-red-700' },
    { id: 'r8to9', label: '8-9 Wins', color: 'bg-orange-600 hover:bg-orange-700' },
    { id: 'r6to7', label: '6-7 Wins', color: 'bg-yellow-600 hover:bg-yellow-700' },
    { id: 'r4to5', label: '4-5 Wins', color: 'bg-blue-600 hover:bg-blue-700' },
    { id: 'lt4', label: '< 4 Wins', color: 'bg-slate-600 hover:bg-slate-700' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-6">
            Scratch Card Statistics
          </h1>

          {/* Date Picker & Filters */}
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            {/* Date Picker */}
            <div className="flex items-center gap-3">
              <label className="text-sm font-semibold text-gray-700">
                Select Date:
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              />
            </div>

            {/* Filter Buttons */}
            <div className="flex flex-wrap gap-2">
              {filterButtons.map(btn => (
                <button
                  key={btn.id}
                  onClick={() => setFilter(btn.id)}
                  className={`px-4 py-2 rounded-lg text-white text-sm font-medium transition-all transform hover:scale-105 ${
                    filter === btn.id 
                      ? `${btn.color} ring-2 ring-offset-2 ring-gray-400` 
                      : `${btn.color} opacity-70`
                  }`}
                >
                  {btn.label}
                </button>
              ))}
            </div>
          </div>

          {/* Stats Summary */}
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-4 text-white">
              <p className="text-sm opacity-90">Total Shops</p>
              <p className="text-2xl font-bold">{filteredData.length}</p>
            </div>
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-4 text-white">
              <p className="text-sm opacity-90">Total Wins</p>
              <p className="text-2xl font-bold">
                {filteredData.reduce((sum, item) => sum + item.win_count, 0)}
              </p>
            </div>
            <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg p-4 text-white">
              <p className="text-sm opacity-90">Total Losses</p>
              <p className="text-2xl font-bold">
                {filteredData.reduce((sum, item) => sum + item.lose_count, 0)}
              </p>
            </div>
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-4 text-white">
              <p className="text-sm opacity-90">Total Games</p>
              <p className="text-2xl font-bold">
                {filteredData.reduce((sum, item) => sum + item.total_games, 0)}
              </p>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-gray-800 to-gray-900 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-bold">Shop ID</th>
                    <th className="px-6 py-4 text-left text-sm font-bold">Shop Name</th>
                    <th className="px-6 py-4 text-center text-sm font-bold">Wins</th>
                    <th className="px-6 py-4 text-center text-sm font-bold">Bags</th>
                    <th className="px-6 py-4 text-center text-sm font-bold">Vouchers</th>
                    <th className="px-6 py-4 text-center text-sm font-bold">Losses</th>
                    <th className="px-6 py-4 text-center text-sm font-bold">Total Games</th>
                    <th className="px-6 py-4 text-center text-sm font-bold">Win Rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredData.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                        No data found for selected filters
                      </td>
                    </tr>
                  ) : (
                    filteredData.map((item, index) => (
                      <tr
                        key={item.shop_id}
                        className={`transition-all ${getRowColor(item.win_count)}`}
                      >
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {item.shop_id}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-800 font-medium">
                          {item.shop_name}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-sm font-bold ${getBadgeColor(item.win_count)}`}>
                            {item.win_count}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center text-sm text-gray-700">
                          {item.bag_count ?? 0}
                        </td>
                        <td className="px-6 py-4 text-center text-sm text-gray-700">
                          {item.voucher_count ?? 0}
                        </td>
                        <td className="px-6 py-4 text-center text-sm text-gray-700">
                          {item.lose_count}
                        </td>
                        <td className="px-6 py-4 text-center text-sm font-semibold text-gray-900">
                          {item.total_games}
                        </td>
                        <td className="px-6 py-4 text-center text-sm text-gray-700">
                          {item.total_games > 0 
                            ? `${((item.win_count / item.total_games) * 100).toFixed(1)}%`
                            : '0%'
                          }
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScratchCardStats;